import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { verifyOTP, verifyUserID } from 'src/services/Login/ForgotPassword';
import { loadingState } from 'src/atom/LoadingState';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LogoHeader from 'src/components/header/LogoHeader';

const ValidatePassword = () => {
  const [formData, setFormData] = useState({ id: '', mobileOTP: '' });
  const [showResendButton, setShowResendButton] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [isChecked, setIsChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState({ mobileOTP: '' });
  const [roleId, setRoleId] = useState('');
  const [token, setToken] = useState('');

  const [loader, enableLoader] = useRecoilState(loadingState);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      setRoleId(location.state.userId || '');
      setToken(location.state.token || '');
    }
  }, [location.state]);

  useEffect(() => {
    let timer = null;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else {
      setShowResendButton(true);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResend = async () => {
    if (!roleId) {
      toast.error('User ID not available', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
      return;
    }
    setShowResendButton(false);
    setCountdown(120);
    try {
      const response = await verifyUserID(roleId);
      const bodyValues = Object.values(response.body);
      setToken(bodyValues);
      if (response.success) {
        toast.success('OTP sent successfully!', {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'colored',
        });
      } else {
        toast.error(response.message || 'Failed to send OTP', {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'colored',
        });
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    }
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;
    if (!formData.mobileOTP) {
      errors.mobileOTP = 'Please provide OTP';
      isValid = false;
    }
    setValidationErrors(errors);
    return isValid;
  };

  const verifyOtp = async () => {
    if (!token) {
      toast.error('Token not available', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
      return;
    }
    enableLoader(true);
    try {
      const response = await verifyOTP(token, formData.mobileOTP);
      if (response.success) {
        navigate('/confirm-new-password', { state: { userId: token } });
      } else {
        toast.error(response.message || 'OTP verification failed', {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'colored',
        });
        setFormData((prev) => ({ ...prev, mobileOTP: '' }));
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while verifying OTP', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    } finally {
      enableLoader(false);
      setIsChecked(false);
    }
  };

  const handleOTPChange = (e) => {
    const { value } = e.target;
    const numericValue = value.replace(/\D/g, '');
    const truncatedValue = numericValue.slice(0, 4);
    setFormData((prev) => ({ ...prev, mobileOTP: truncatedValue || '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsChecked(true);
    if (validateForm()) {
      verifyOtp();
    }
  };

  return (
    <>
      <div className="card">
        <LogoHeader />
      </div>
      <div className="container">
        <div className="custom-box">
          <div className="card ms-3 me-3 mt-3">
            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  <strong className="password-heading">Verify OTP</strong>
                </div>
              </div>
              <hr />
              <div className="row mt-2">
                <div className="col-12">
                  <div className="d-flex flex-column align-items-start">
                    <label className="form-label">
                      Enter the OTP sent to your registered email and mobile number
                    </label>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="row justify-content-center">
                      <div className="col-12 d-flex justify-content-center align-items-center">
                        <input
                          type="text"
                          className={`form-control ${
                            validationErrors.mobileOTP ? 'is-invalid' : ''
                          }`}
                          id="OTP"
                          maxLength="4"
                          placeholder="Enter One-Time Password"
                          value={formData.mobileOTP}
                          onChange={handleOTPChange}
                        />
                        {validationErrors.mobileOTP && (
                          <div className="invalid-feedback">{validationErrors.mobileOTP}</div>
                        )}
                        <button
                          className="ms-2 btn btn-primary"
                          type="submit"
                          disabled={loader || !formData.mobileOTP || isChecked}
                        >
                          {loader ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            'Verify OTP'
                          )}
                        </button>
                        {showResendButton && (
                          <button
                            className="ms-2 btn btn-danger"
                            type="button"
                            onClick={handleResend}
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                  <div className="toolbar mt-2">
                    <span>
                      <Link to="/login" className="back-link">
                        Back to Login
                      </Link>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 70vh;
            background-color: #f8f9fa;
            padding: 1rem;
          }

          .custom-box {
            width: 100%;
            max-width: 600px;
            background-color: #ffffff;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            padding: 1.5rem;
          }

          .card {
            border: none;
            border-radius: 8px;
            background-color: transparent;
          }

          .password-heading {
            display: flex;
            justify-content: start;
            align-items: center;
            font-size: 1.25rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }

          .form-label {
            color: #dc3545;
            font-size: 0.9rem;
            margin-bottom: 1rem;
          }

          .form-control {
            border-radius: 4px;
            padding: 0.5rem 0.75rem;
            font-size: 1rem;
            height: 38px;
          }

          .btn-primary,
          .btn-danger {
            border-radius: 4px;
            font-size: 1rem;
            height: 38px;
            line-height: 1.5;
            transition: background-color 0.2s ease;
            white-space: nowrap;
          }

          .btn-primary {
            padding: 0.5rem 1.5rem;
          }

          .btn-primary:hover {
            background-color: #0056b3;
          }

          .btn-danger {
            padding: 0.5rem 1rem;
          }

          .btn-danger:hover {
            background-color: #c82333;
          }

          .toolbar {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 1rem;
          }

          .back-link {
            text-decoration: none;
            color: #007bff;
            font-weight: 500;
            transition: color 0.2s ease;
          }

          .back-link:hover {
            color: #0056b3;
            text-decoration: underline;
          }

          @media (max-width: 576px) {
            .custom-box {
              padding: 1rem;
            }

            .form-control,
            .btn-primary,
            .btn-danger {
              font-size: 0.9rem;
              height: 34px;
            }

            .btn-primary {
              padding: 0.5rem 1rem;
            }

            .btn-danger {
              padding: 0.5rem 0.75rem;
            }
          }
        `}
      </style>
      <ToastContainer />
    </>
  );
};

export default ValidatePassword;